/**
 * Estado operacional por transação documental (lista + chip + Gerenciar).
 * Prioridade alinhada à lista do médico; lojas usam a ordem “pior → melhor”.
 * @module transactionEcommerceOpState
 */

import type { DocumentReference } from "fhir/r5";
import { extractDocumentVerification } from "src-next/modules/patient-supporting-documents";

import {
  collectTransactionDocuments,
  type DocumentTransaction,
  type TransactionOpState,
} from "./documentTransactions";
import {
  peekPatientReleaseForTransaction,
  peekTransactionPurchaseCompleted,
} from "./transactionReleaseLookup";

const REQUIRED_TX_DOCS = 4;

function verificationOf(
  doc: DocumentReference | undefined,
  organizationId?: string,
) {
  if (!doc) return { status: "none" as const };
  return extractDocumentVerification(doc, organizationId);
}

/**
 * Lista do médico — rejected vence pending.
 */
export function resolveTransactionOpState(
  transaction: DocumentTransaction,
  patientKey: string,
  organizationId?: string,
): TransactionOpState {
  if (peekTransactionPurchaseCompleted(patientKey, transaction.transactionId)) {
    return "purchase_completed";
  }

  const docs = collectTransactionDocuments(transaction);
  const verifications = [
    verificationOf(transaction.sharedDocs.personal, organizationId),
    verificationOf(transaction.sharedDocs.residence, organizationId),
    verificationOf(transaction.prescriptionDoc, organizationId),
    verificationOf(transaction.anvisaDoc, organizationId),
  ];

  if (verifications.some((item) => item.status === "rejected")) {
    return "docs_rejected";
  }

  if (docs.length < REQUIRED_TX_DOCS) {
    return "docs_pending";
  }

  if (verifications.some((item) => item.status !== "approved")) {
    return "docs_to_approve";
  }

  if (peekPatientReleaseForTransaction(patientKey, transaction.transactionId)) {
    return "docs_released";
  }

  return "awaiting_release";
}

/**
 * Chip da loja (pior → melhor), escopo organizationId da store.
 */
export function resolveStoreDocumentOpState(
  transaction: DocumentTransaction,
  patientKey: string,
  organizationId?: string,
): TransactionOpState {
  const verifications = [
    verificationOf(transaction.sharedDocs.personal, organizationId),
    verificationOf(transaction.sharedDocs.residence, organizationId),
    verificationOf(transaction.prescriptionDoc, organizationId),
    verificationOf(transaction.anvisaDoc, organizationId),
  ];
  const presentCount = collectTransactionDocuments(transaction).length;

  if (presentCount < REQUIRED_TX_DOCS) return "docs_pending";
  if (verifications.some((item) => item.status === "rejected")) {
    return "docs_rejected";
  }
  if (verifications.some((item) => item.status !== "approved")) {
    return "docs_to_approve";
  }
  if (peekTransactionPurchaseCompleted(patientKey, transaction.transactionId)) {
    return "purchase_completed";
  }
  if (peekPatientReleaseForTransaction(patientKey, transaction.transactionId)) {
    return "docs_released";
  }
  return "awaiting_release";
}

/** Pior estado entre transações (chip da loja no picker). */
export function resolveWorstStoreOpState(
  transactions: DocumentTransaction[],
  patientKey: string,
  organizationId?: string,
): TransactionOpState {
  const order: TransactionOpState[] = [
    "docs_pending",
    "docs_rejected",
    "docs_to_approve",
    "awaiting_release",
    "docs_released",
    "purchase_completed",
  ];
  if (!transactions.length) return "docs_pending";

  let worst: TransactionOpState = "docs_released";
  let worstRank = order.length;
  for (const transaction of transactions) {
    const state = resolveStoreDocumentOpState(
      transaction,
      patientKey,
      organizationId,
    );
    const rank = order.indexOf(state);
    if (rank >= 0 && rank < worstRank) {
      worst = state;
      worstRank = rank;
    }
  }
  return worst;
}
